import { AxiosError, AxiosRequestConfig } from 'axios';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import AuthAction from 'redux/actions/authActions/Actions';
import DashboardConfigAction from 'redux/actions/dashboardConfigActions/Action';
import IntlAction from 'redux/actions/intlActions/Actions';
import { persistor, store } from 'redux/reducers';

import i18n from '../config/i18n';
import { FunctionsHelper } from './FunctionsHelper';
import instance from './MainBackendAxios';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Endpoints de auth donde un 401 es respuesta de negocio, no sesión expirada. */
const AUTH_ENDPOINTS_SKIP_SESSION_CLEAR = [
  '/auth/login',
  '/auth/get-current-user',
];

let isHandlingUnauthorized = false;

const clearSessionAndRedirectToLogin = () => {
  if (isHandlingUnauthorized) {
    return;
  }
  isHandlingUnauthorized = true;

  persistor.purge();
  store.dispatch(AuthAction.signOut({}));
  store.dispatch(DashboardConfigAction.remove());

  const path = window.location.pathname || '';
  const isPublicAuthPage =
    path.startsWith('/login') || path.startsWith('/reset-password');

  if (!isPublicAuthPage) {
    window.location.assign('/login');
  } else {
    isHandlingUnauthorized = false;
  }
};

const setUpInterceptor = () => {
  const handleError = async (error: AxiosError) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    const isAuthBusinessEndpoint = AUTH_ENDPOINTS_SKIP_SESSION_CLEAR.some(
      (endpoint) => requestUrl.includes(endpoint)
    );

    if (status === 401 && !isAuthBusinessEndpoint) {
      clearSessionAndRedirectToLogin();
    }

    return Promise.reject(error);
  };

  instance.interceptors.request.use(
    async (config: any | AxiosRequestConfig) => {
      config.headers.timezone = dayjs.tz.guess();

      // 👇 Añadimos el header de idioma actual
      config.headers.lang = i18n.language || 'en';

      const token = store.getState().auth.token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    handleError
  );

  instance.interceptors.response.use((response) => response, handleError);

  // 👇 Actualiza el header por defecto si el idioma cambia
  i18n.on('languageChanged', (lng) => {
    instance.defaults.headers.lang = lng;
    store.dispatch(
      IntlAction.update({
        lang: lng,
        dateFormat: FunctionsHelper.getDateFormatByLang(lng),
      })
    );
  });
};

export default setUpInterceptor;
